import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { POS } from "@/constants/testIds";
import { toast } from "sonner";
import PostTradeReviewModal from "@/components/PostTradeReviewModal";
import PositionReviewDialog from "@/components/PositionReviewDialog";
import { useConfirm } from "@/components/ConfirmDialog";
import { ShieldCheck, Brain, Clock } from "lucide-react";
import { Ticker } from "@/components/TickerDrawer";

export default function Positions() {
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewFor, setReviewFor] = useState(null);
  const [aiReviewPos, setAiReviewPos] = useState(null);
  const [source, setSource] = useState(() =>
    (localStorage.getItem("tm_positions_source")
     || localStorage.getItem("tm_execution_source")
     || "ALPACA_PAPER").toUpperCase());
  const nav = useNavigate();
  const confirm = useConfirm();

  const load = async (srcOverride) => {
    setLoading(true);
    try {
      const src = (srcOverride || source).toUpperCase();
      // Pass the explicit source so the api interceptor's global
      // tm_execution_source value doesn't override our per-page pick.
      const { data } = await api.get("/positions", {
        params: { execution_source: src }});
      setPositions(data);
    } catch (e) {
      // ignore
    } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    localStorage.setItem("tm_positions_source", source);
    const t = setInterval(() => load(), 20000);
    return () => clearInterval(t);
  }, [source]); // eslint-disable-line react-hooks/exhaustive-deps

  const close = async (p, reason) => {
    const isDestructive = reason === "stop_hit";
    const ok = await confirm.confirm({
      variant: isDestructive ? "destructive" : "confirm",
      title: `Close ${p.symbol}?`,
      description: `Realize the current P&L and exit this position. Reason: ${reason.replace("_", " ")}.`,
      details: {
        symbol: p.symbol, qty: p.qty,
        entry: `$${p.entry_price?.toFixed(2)}`,
        current: `$${p.current_price?.toFixed(2)}`,
        unrealized: `${p.unrealized_pnl >= 0 ? "+" : ""}$${p.unrealized_pnl?.toFixed(2)}`,
        bucket: p.portfolio_key || "—",
      },
      confirmLabel: isDestructive ? "Close at Stop" : "Close Position",
      testId: `close-position-confirm-${p.id}`,
    });
    if (!ok) return;
    try {
      const { data } = await api.post(`/positions/${p.id}/close`, { reason });
      toast.success(`Closed ${p.symbol} · ${data.realized_pnl >= 0 ? "+" : ""}$${data.realized_pnl}`);
      if (data.journal_id) setReviewFor(data.journal_id);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const open = positions.filter((p) => p.status === "open");
  const closed = positions.filter((p) => p.status === "closed").slice(0, 20);

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid={POS.root}>
      <div className="flex items-center flex-wrap gap-2">
        <h1 className="text-xl font-medium tracking-tight">Positions</h1>
        <div className="ml-auto flex items-center gap-2">
          <span className="label-cap">Source</span>
          {[
            { id: "ALPACA_PAPER",       label: "Alpaca Paper" },
            { id: "INTERNAL_PAPER_MOCK", label: "Internal Paper" },
            { id: "ALL",                  label: "All" },
          ].map((s) => (
            <button
              key={s.id}
              onClick={() => setSource(s.id)}
              data-testid={`positions-source-${s.id}`}
              className={`chip ${source === s.id ? "!text-foreground !border-foreground" : ""}`}
            >{s.label}</button>
          ))}
        </div>
      </div>
      <div className="text-[10px] mono uppercase tracking-widest text-muted-foreground" data-testid="positions-source-label">
        Showing: {source.replace(/_/g, " ")}
      </div>

      {!loading && open.length === 0 && closed.length === 0 && source === "ALPACA_PAPER" && (
        <div className="grid-cell !p-4 text-sm" data-testid="positions-empty-alpaca-cta">
          <div className="mb-2">No Alpaca Paper positions found in your history.</div>
          <button
            onClick={() => setSource("INTERNAL_PAPER_MOCK")}
            className="btn-outline h-8 !px-3 text-[11px]"
            data-testid="positions-view-mock-history-btn"
          >View Internal Paper History →</button>
        </div>
      )}

      <section className="bg-card border border-border/60">
        <div className="flex items-center px-4 h-10 border-b border-border/60">
          <div className="label-cap">Open</div>
          <div className="ml-auto text-[10px] mono text-muted-foreground uppercase tracking-widest">{open.length} live</div>
        </div>
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-widest text-muted-foreground bg-background/20">
            <tr>
              <th className="text-left px-4 py-1.5 font-normal">Symbol</th>
              <th className="text-center px-4 py-1.5 font-normal">Bucket</th>
              <th className="text-right px-4 py-1.5 font-normal">Qty</th>
              <th className="text-right px-4 py-1.5 font-normal">Entry</th>
              <th className="text-right px-4 py-1.5 font-normal">Mkt</th>
              <th className="text-right px-4 py-1.5 font-normal">Stop</th>
              <th className="text-right px-4 py-1.5 font-normal">Target</th>
              <th className="text-right px-4 py-1.5 font-normal">Unrealized</th>
              <th className="text-center px-4 py-1.5 font-normal">Protection</th>
              <th className="text-right px-4 py-1.5 font-normal"></th>
            </tr>
          </thead>
          <tbody>
            {open.map((p) => {
              const stopHit = p.current_price <= p.stop_price;
              const tgtHit = p.current_price >= p.target_price;
              return (
                <tr key={p.id} className="border-t border-border/40 hover:bg-secondary/30" data-testid={POS.row(p.id)}>
                  <td className="px-4 py-2 mono"><Ticker symbol={p.symbol}/></td>
                  <td className="px-4 py-2 text-center">
                    {p.portfolio_key ? <span className="chip !text-[9px] text-muted-foreground border-current">{p.portfolio_key.replace("_"," ")}</span> : <span className="text-[10px] text-muted-foreground">—</span>}
                  </td>
                  <td className="px-4 py-2 mono text-right">{p.qty}</td>
                  <td className="px-4 py-2 mono text-right">${p.entry_price?.toFixed(2)}</td>
                  <td className="px-4 py-2 mono text-right">${p.current_price?.toFixed(2)}</td>
                  <td className={`px-4 py-2 mono text-right ${stopHit ? "pnl-down" : ""}`}>${p.stop_price?.toFixed(2)}</td>
                  <td className={`px-4 py-2 mono text-right ${tgtHit ? "pnl-up" : ""}`}>${p.target_price?.toFixed(2)}</td>
                  <td className={`px-4 py-2 mono text-right ${p.unrealized_pnl >= 0 ? "pnl-up" : "pnl-down"}`}>
                    {p.unrealized_pnl >= 0 ? "+" : ""}${p.unrealized_pnl?.toFixed(2)} <span className="text-[10px] opacity-70">({p.unrealized_pnl_pct?.toFixed(2)}%)</span>
                  </td>
                  <td className="px-4 py-2 text-center">
                    {p.protection_status === "armed" ? (
                      <span className="chip pnl-up border-current text-[9px] !py-0.5"><ShieldCheck size={10}/> ARMED</span>
                    ) : p.protection_status === "unknown" ? (
                      <span className="chip text-signal-yellow border-current text-[9px] !py-0.5">UNKNOWN</span>
                    ) : (
                      <span className="chip text-muted-foreground border-current text-[9px] !py-0.5">MANUAL</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <div className="inline-flex gap-1.5">
                      <button className="btn-outline h-7 !px-2 text-[11px]" onClick={()=>setAiReviewPos(p)} data-testid={`review-position-${p.id}`}>
                        <Brain size={12}/> Review
                      </button>
                      {stopHit && <button className="btn-danger h-7 !px-2 text-[11px]" onClick={()=>close(p,"stop_hit")}>Stop</button>}
                      {tgtHit && <button className="btn-primary h-7 !px-2 text-[11px]" onClick={()=>close(p,"target_hit")}>Target</button>}
                      <button className="btn-outline h-7 !px-2 text-[11px]" onClick={()=>nav(`/replay/${p.id}`)} data-testid={`replay-position-${p.id}`}>
                        <Clock size={12}/> Replay
                      </button>
                      <button className="btn-outline h-7 !px-2 text-[11px]" onClick={()=>close(p,"manual")} data-testid={POS.closeBtn(p.id)}>Close</button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {loading && <tr><td colSpan={10} className="px-4 py-6 text-center text-xs text-muted-foreground">Loading...</td></tr>}
            {!loading && open.length === 0 && <tr><td colSpan={10} className="px-4 py-8 text-center text-xs text-muted-foreground">No open positions.</td></tr>}
          </tbody>
        </table>
      </section>

      <section className="bg-card border border-border/60">
        <div className="flex items-center px-4 h-10 border-b border-border/60">
          <div className="label-cap">Recently Closed</div>
        </div>
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-widest text-muted-foreground bg-background/20">
            <tr>
              <th className="text-left px-4 py-1.5 font-normal">Symbol</th>
              <th className="text-center px-4 py-1.5 font-normal">Bucket</th>
              <th className="text-right px-4 py-1.5 font-normal">Entry</th>
              <th className="text-right px-4 py-1.5 font-normal">Exit</th>
              <th className="text-right px-4 py-1.5 font-normal">Reason</th>
              <th className="text-right px-4 py-1.5 font-normal">Realized</th>
              <th className="text-right px-4 py-1.5 font-normal">Replay</th>
            </tr>
          </thead>
          <tbody>
            {closed.map((p) => (
              <tr key={p.id} className="border-t border-border/40 hover:bg-secondary/30">
                <td className="px-4 py-2 mono"><Ticker symbol={p.symbol}/></td>
                <td className="px-4 py-2 text-center">
                  {p.portfolio_key ? <span className="chip !text-[9px] text-muted-foreground border-current">{p.portfolio_key.replace("_"," ")}</span> : <span className="text-[10px] text-muted-foreground">—</span>}
                </td>
                <td className="px-4 py-2 mono text-right">${p.entry_price?.toFixed(2)}</td>
                <td className="px-4 py-2 mono text-right">${p.close_price?.toFixed(2)}</td>
                <td className="px-4 py-2 text-right text-xs uppercase mono text-muted-foreground tracking-widest">{p.close_reason?.replace("_"," ")}</td>
                <td className={`px-4 py-2 mono text-right ${p.realized_pnl >= 0 ? "pnl-up" : "pnl-down"}`}>{p.realized_pnl >= 0 ? "+" : ""}${p.realized_pnl?.toFixed(2)}</td>
                <td className="px-4 py-2 text-right">
                  <button className="btn-outline h-7 !px-2 text-[11px]" onClick={()=>nav(`/replay/${p.id}`)} data-testid={`replay-closed-${p.id}`}>
                    <Clock size={12}/> Replay
                  </button>
                </td>
              </tr>
            ))}
            {closed.length === 0 && <tr><td colSpan={7} className="px-4 py-6 text-center text-xs text-muted-foreground">Nothing closed yet.</td></tr>}
          </tbody>
        </table>
      </section>

      <PostTradeReviewModal
        open={!!reviewFor}
        journalId={reviewFor}
        onClose={() => setReviewFor(null)}
        onSubmitted={load}
      />

      <PositionReviewDialog
        open={!!aiReviewPos}
        position={aiReviewPos}
        onClose={() => setAiReviewPos(null)}
        onApplied={load}
      />
    </div>
  );
}
